import React, { useEffect, useState } from 'react';
import { useLocation, useParams, matchPath } from 'react-router-dom';

/**
 * Floating WhatsApp button - visible on all pages.
 * Shows real WhatsApp icon + "Canlı Sohbet" label pill.
 * On product pages, includes product name in the pre-filled message.
 */
const WhatsAppButton = () => {
  const phone = '905518348883';
  const location = useLocation();
  const [productName, setProductName] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  useEffect(() => {
    // Check if we're on a product page: /product/:id
    const match = matchPath('/product/:id', location.pathname);
    if (!match) {
      setProductName(null);
      return;
    }
    
    const productId = match.params.id;
    let cancelled = false;
    
    fetch(`${API_URL}/api/products/${productId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!cancelled && data && data.name) {
          setProductName(data.name);
        }
      })
      .catch(() => {});
    
    return () => { cancelled = true; };
  }, [location.pathname, API_URL]);
  
  const defaultMsg = 'Merhaba, Cep Premium hakkında bilgi almak istiyorum.';
  const productMsg = productName
    ? `Merhaba, "${productName}" ürünü hakkında bilgi alabilir miyim?`
    : defaultMsg;
  const message = encodeURIComponent(productMsg);
  
  return (
    <a
      href={`https://wa.me/${phone}?text=${message}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Canlı Sohbet - WhatsApp"
      data-testid="whatsapp-chat-button"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-3 group"
    >
      {/* WhatsApp icon circle */}
      <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110">
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-40"></span>
        {/* Real WhatsApp SVG */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 32 32"
          className="relative w-8 h-8 text-white fill-current"
          aria-hidden="true"
        >
          <path d="M16.004 3C9.374 3 4 8.374 4 15.004c0 2.333.64 4.515 1.755 6.393L4 29l7.79-1.732A11.95 11.95 0 0016.004 27C22.634 27 28 21.634 28 15.004 28 8.374 22.634 3 16.004 3zm0 21.818a9.82 9.82 0 01-5.01-1.371l-.358-.212-4.627 1.028 1.048-4.5-.233-.37a9.818 9.818 0 1118.998-3.39c0 5.42-4.41 9.815-9.818 9.815zm5.386-7.352c-.295-.148-1.746-.862-2.017-.96-.271-.098-.469-.148-.666.148-.197.295-.764.96-.937 1.158-.172.197-.345.222-.64.074-.295-.148-1.246-.459-2.374-1.463-.878-.783-1.47-1.75-1.643-2.045-.172-.296-.018-.455.13-.603.133-.132.295-.345.443-.517.148-.173.197-.296.295-.493.099-.197.05-.37-.024-.517-.074-.148-.666-1.605-.912-2.197-.24-.577-.485-.498-.666-.508-.172-.008-.37-.01-.567-.01a1.09 1.09 0 00-.79.37c-.271.296-1.034 1.01-1.034 2.463 0 1.454 1.058 2.858 1.206 3.055.148.197 2.084 3.181 5.05 4.461.706.305 1.256.487 1.685.623.708.225 1.352.193 1.862.117.568-.085 1.746-.714 1.993-1.403.246-.69.246-1.28.172-1.403-.073-.123-.271-.197-.567-.345z"/>
        </svg>
      </span>
      
      {/* Label pill - different text on product pages */}
      <span 
        data-testid="whatsapp-chat-label"
        className="hidden sm:inline-flex items-center bg-[#25D366] text-white font-semibold px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group-hover:bg-[#1DA851] whitespace-nowrap"
      >
        {productName ? 'Bu ürün hakkında bilgi alabilir miyim?' : 'Canlı Sohbet'}
      </span>
    </a>
  );
};

export default WhatsAppButton;
